import Vue from 'vue';
import Vuex, { MutationTree, ActionTree } from 'vuex';
import type { PMLesson } from '@/models/pmlesson';
import type { Lesson } from '@/models/lesson';
import { lessonWebservice } from '@/webservices/lesson';
import { pmLessonWebservice } from '@/webservices/pmlesson';
import type { XLesson } from '@/models/xlesson';
import { xLessonWebservice } from '@/webservices/xlesson';
import { hasPermission } from '@/helpers/auth/permissionHelpers';
import { PERMISSIONS } from '@/helpers/auth/permissions';

Vue.use(Vuex);

interface State {
    openLessons: Lesson[];
    openPMLessons: PMLesson[];
    openXLessons: XLesson[];
}

const mutations: MutationTree<State> = {
    setOpenLessons(currentState, openLessons: Lesson[]) {
        currentState.openLessons = openLessons;
    },

    setOpenPMLessons(currentState, openPMLessons: PMLesson[]) {
        currentState.openPMLessons = openPMLessons;
    },

    setOpenXLessons(currentState, openXLessons: XLesson[]) {
        currentState.openXLessons = openXLessons;
    },

    resetState() {
        // console.log('resetState FIRED FROM Account STORE');
        // acquire initial state
        const s = initialState();

        Object.keys(s).forEach((key) => {
            state[key] = s[key];
        });
    },
};

const actions: ActionTree<State, any> = {
    fetchAllOpen({ commit }): any {
        if (hasPermission(PERMISSIONS.APPEX_LESSON_APPROVE)) {
            lessonWebservice.loadLessonsByApproved(false).then((responseLesson) => {
                const openLessons: Lesson[] = responseLesson && responseLesson.data;
                commit('setOpenLessons', openLessons);
            });
        }
        if (hasPermission(PERMISSIONS.APPEX_PM_LESSON_APPROVE)) {
            pmLessonWebservice.loadLessonsByApproved(false).then((responsePMLesson) => {
                const openPMLessons: PMLesson[] = responsePMLesson && responsePMLesson.data;
                commit('setOpenPMLessons', openPMLessons);
            });
        }
        if (hasPermission(PERMISSIONS.APPEX_X_LESSON_APPROVE)) {
            xLessonWebservice.loadLessonsByApproved(false).then((responseXLesson) => {
                const openXLessons: XLesson[] = responseXLesson && responseXLesson.data;
                commit('setOpenXLessons', openXLessons);
            });
        }
    },
};

const getters = {
    openLessons(currentState: State): Lesson[] {
        return currentState.openLessons;
    },

    openPMLessons(currentState: State): PMLesson[] {
        return currentState.openPMLessons;
    },

    openXLessons(currentState: State): XLesson[] {
        return currentState.openXLessons;
    },

    countAllOpenLessons(currentState: State): number {
        return currentState.openLessons.length + currentState.openPMLessons.length + currentState.openXLessons.length;
    },
};

function initialState() {
    return {
        openLessons: Array<Lesson>(),
        openPMLessons: Array<PMLesson>(),
        openXLessons: Array<XLesson>(),
    };
}

const state: State = initialState();

const module = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};

export default module;
